import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import {
  ArrowLongRightIcon,
  ArrowPathIcon,
  Bars3Icon,
  ChartPieIcon,
  CursorArrowRaysIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import Link from "next/link";
import { Slide } from "react-awesome-reveal";
import LanguageSwitcherFlags from "./LanguageSwitcher";
import { useTranslation } from "next-i18next";
import { Building2, Warehouse } from "lucide-react";

const products = [
  {
    key: "basic",
    icon: ChartPieIcon,
    href: "/basic",
  },
  {
    key: "full",
    icon: CursorArrowRaysIcon,
    href: "/full",
  },
  {
    key: "modules",
    icon: ArrowPathIcon,
    href: "/modules",
  },
];

export default function FrontNav({ dark }) {
  const { t } = useTranslation("frontNav");

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const SCROLL_SENSITIVITY = 10;
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (localStorage.getItem("token")) {
      setLoggedIn(true);
    }

    const handleScroll = () => {
      const scrollY = window.scrollY;

      if (scrollY > lastScrollY.current + SCROLL_SENSITIVITY) {
        setShowHeader(false);
      } else if (scrollY < lastScrollY.current - SCROLL_SENSITIVITY) {
        setShowHeader(true);
      }

      setIsScrolled(scrollY > 5);
      lastScrollY.current = scrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`
        sticky top-0 left-0 w-full z-10 duration-300 transform px-8 border-b
        ${
          isScrolled
            ? "backdrop-blur bg-white/60 shadow"
            : "bg-transparent text-gray-900"
        }
        ${showHeader ? "translate-y-0" : "-translate-y-full"}
      `}
    >
      <Slide direction="down" triggerOnce>
        <nav
          className="mx-auto flex max-w-screen-2xl items-center justify-between p-4"
          aria-label="Global"
        >
          {/* Logo */}
          <div className="flex lg:flex-1">
            <Link href="/">
              <svg
                className="h-5"
                viewBox="0 0 585 121"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {" "}
                <path
                  d="M359.943 18.941C358.96 19.3623 357.485 20.3454 356.642 21.1179C355.614 22.0573 355.027 23.3863 355.027 24.7795V68.0257V111.319C355.027 112.678 355.616 113.971 356.642 114.863C360.294 118.164 363.173 118.655 366.754 116.478C370.336 114.372 370.827 112.757 371.178 102.434C371.459 94.4289 371.67 92.9543 372.653 92.9543C373.566 92.9543 380.939 99.3444 388.663 106.647C391.964 109.878 399.829 116.408 401.163 117.11C405.236 119.217 411.064 116.619 412.188 112.054C413.241 107.771 411.836 104.751 406.429 100.117C403.831 97.94 400.32 94.78 398.565 93.2351C396.809 91.6201 393.438 88.6005 391.051 86.4939C387.118 83.0531 385.574 80.6655 387.189 80.6655C387.47 80.6655 388.944 79.4718 390.489 77.9971C392.034 76.5927 396.037 73.1519 399.337 70.343C410.011 61.3547 410.994 59.8098 408.676 54.9645C406.57 50.6108 401.444 48.5744 397.652 50.6108C395.756 51.5939 382.975 62.5484 377.147 68.1661C373.987 71.1856 372.793 71.9581 372.091 71.3963C371.389 70.8345 371.178 65.568 371.178 47.7317C371.178 26.244 371.108 24.6991 369.774 22.5925C368.369 20.205 365.069 18.1686 362.962 18.2388C362.26 18.2388 360.926 18.5899 359.943 18.941Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M429.671 19.573C425.106 22.3116 425.247 20.6965 425.247 68.1661C425.247 109.597 425.317 111.282 426.651 113.459C428.126 115.846 431.286 117.883 433.673 117.883C435.92 117.883 439.01 116.057 440.695 113.67C441.889 111.984 442.451 111.703 443.364 112.265C448.279 115.285 449.894 116.057 453.195 116.9C474.963 122.588 496.381 101.802 492.659 78.6291C491.184 69.4301 486.831 62.1973 479.528 56.6498C474.823 53.0685 472.646 52.0854 467.379 50.7512C459.655 48.8553 452.352 49.8384 446.102 53.4899C446.022 53.5375 445.942 53.5844 445.863 53.6307C443.662 54.9128 442.203 53.8794 442.115 51.3339C442.012 48.3497 441.928 43.9079 441.889 39.0243C441.749 21.4689 441.678 21.2583 436.693 19.1516C433.673 17.8174 432.339 17.8876 429.671 19.573ZM468.011 67.6043C475.736 72.5198 479.387 82.8424 476.017 90.2858C474.261 94.148 470.61 98.5719 467.52 100.468C465.273 101.872 464.009 102.083 458.602 102.083C452.984 102.083 452.001 101.872 449.473 100.257C446.383 98.2208 444.557 96.1844 442.451 92.5329C441.397 90.6369 441.117 88.8112 441.117 83.8255C441.117 78.8398 441.397 77.014 442.451 75.0478C444.066 72.239 447.928 68.0257 448.981 68.0257C449.332 68.0257 450.035 67.6043 450.526 67.0426C452.492 64.6551 463.939 65.0062 468.011 67.6043Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M235.789 25.6121C228.837 31.0894 230.593 40.9906 238.879 43.4484C247.025 45.9061 254.609 36.9178 250.746 29.4041C247.867 23.8566 240.424 21.9606 235.789 25.6121Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M511.619 26.4548C508.74 29.053 508.599 29.2636 508.529 33.3365C508.459 38.4626 510.355 41.3417 514.99 43.1675C525.172 47.1701 533.107 33.6876 524.891 26.2441C522.503 24.0673 521.731 23.7864 518.36 23.7864C514.99 23.7864 514.287 24.0673 511.619 26.4548Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M557.614 34.1089C553.752 35.092 552.066 38.252 551.645 45.2039L551.294 50.8216L547.572 51.1727C542.376 51.7345 540.059 54.122 540.059 58.897C540.059 63.5316 542.165 65.7085 546.87 66.2C548.696 66.4107 550.522 66.6916 550.943 66.9725C551.364 67.2534 551.645 73.5031 551.645 84.0363C551.645 103.066 551.996 105.032 556.42 110.159C560.633 115.074 564.215 116.479 572.501 116.479C578.891 116.479 579.242 116.408 581.138 114.442C584.579 111.001 584.79 107.069 581.63 103.417C580.225 101.802 579.312 101.521 575.029 101.24C570.886 100.889 569.903 100.608 569.271 99.3445C568.709 98.3614 568.498 92.8841 568.639 82.4212L568.849 66.9725L575.099 66.4809C580.576 66.0596 581.559 65.7787 583.174 64.1636C584.649 62.7592 585 61.7059 585 58.897C585 56.018 584.649 55.1051 583.034 53.49C581.279 51.7345 580.506 51.5238 574.959 51.1727L568.849 50.8216L568.498 44.6421C568.147 39.0244 567.936 38.3924 566.04 36.4964C563.934 34.3898 560.353 33.4067 557.614 34.1089Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M173.502 50.7514C164.163 52.858 155.175 60.1611 150.68 69.1494C148.363 73.9244 148.293 74.1351 148.223 83.1234C148.223 90.1456 148.504 92.9544 149.416 95.0611C150.048 96.6059 150.751 98.3615 151.032 98.9232C152.998 103.769 160.371 111.282 165.848 114.021C178.628 120.411 194.99 118.445 204.821 109.316C207.63 106.648 211.773 101.521 211.773 100.538C211.773 100.187 212.124 99.5552 212.545 99.1339C215.916 95.7633 217.25 80.2444 214.722 73.2924C208.683 56.1584 191.339 46.6786 173.502 50.7514ZM190.004 66.9725C190.636 67.5343 191.339 68.0258 191.619 68.0258C192.181 68.0258 197.026 72.8711 197.026 73.4329C197.026 73.7138 197.448 74.416 197.939 74.9778C198.501 75.6098 199.274 77.7164 199.695 79.7528C201.45 87.6176 197.799 95.6931 190.355 100.679C188.67 101.802 187.055 102.083 181.929 102.083C176.241 102.083 175.328 101.873 172.66 100.187C167.393 96.7464 163.32 89.4433 163.32 83.3341C163.32 76.0311 170.061 67.3939 177.013 65.7788C180.314 65.0766 188.74 65.849 190.004 66.9725Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M294.283 50.8218C282.275 53.8413 273.568 62.4083 269.776 74.9779C268.863 77.9974 268.652 82.0703 268.652 95.5528V109.129C268.652 111.227 269.486 113.239 270.97 114.723C272.304 116.057 274.2 117.321 275.253 117.602C278.273 118.304 282.486 116.619 284.101 114.021C285.365 111.914 285.505 110.44 285.505 95.5528C285.505 81.7192 285.646 79.0508 286.769 76.6632C288.665 72.4499 291.053 69.5006 294.213 67.5345C296.741 65.8491 297.864 65.6385 303.061 65.6385C308.327 65.5683 309.381 65.8491 311.838 67.394C315.35 69.7113 316.543 71.0455 318.65 75.0481C320.195 78.0677 320.265 79.0508 320.616 95.4825C320.967 114.513 320.967 114.513 325.672 116.9C329.394 118.796 333.677 117.392 336.065 113.459C337.329 111.423 337.469 109.878 337.469 95.4123C337.399 75.1886 336.627 71.6775 330.658 63.6723C322.091 52.3666 307.906 47.3809 294.283 50.8218Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M237.276 52.1766C236.433 52.7384 235.099 54.2833 234.326 55.6175C232.922 57.8646 232.852 58.8477 232.852 84.338C232.852 109.969 232.922 110.811 234.326 113.269C237.627 118.817 243.876 119.378 248.09 114.393C249.634 112.567 249.634 112.427 249.705 84.338V61.9289C249.705 58.9679 248.851 56.0697 247.247 53.5811C245.113 51.4598 244.227 51.1233 241.77 51.1233C240.155 51.1233 238.118 51.6149 237.276 52.1766Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M513.024 53.2092C511.467 54.4991 510.566 56.4156 510.566 58.4374V84.3172C510.566 112.406 510.637 113.389 511.971 114.793C515.201 118.164 518.923 118.796 522.715 116.479C523.979 115.706 525.313 114.231 525.734 113.178C526.156 112.055 526.366 100.96 526.226 83.2639L526.039 58.4373C526.024 56.4137 525.116 54.5003 523.557 53.2092C520.257 50.4003 516.325 50.4003 513.024 53.2092Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M60.482 0H120.769V60.9621L112.035 59.8891C81.9829 56.1974 59.6616 30.2665 60.482 0Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M61.1562 59.2258L0.0012207 59.2258L0.0012207 -2.28882e-05L1.92944 0.00606918C34.5939 0.109272 61.0492 26.5614 61.1562 59.2258Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M120.766 59.9563L120.766 120.574H59.8035L60.9769 111.554C64.868 81.6441 90.6078 59.4305 120.766 59.9563Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M61.155 59.8046C61.155 76.852 47.465 90.6715 30.5775 90.6715C13.69 90.6715 0 76.852 0 59.8046C0 42.7573 13.69 28.9377 30.5775 28.9377C47.465 28.9377 61.155 42.7573 61.155 59.8046Z"
                  fill="currentColor"
                />{" "}
                <path
                  d="M0.582031 120.574C0.582031 103.855 14.1354 90.0928 30.8543 90.0928C47.5732 90.0928 61.1266 103.855 61.1266 120.574H0.582031Z"
                  fill="currentColor"
                />{" "}
              </svg>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">{t("nav.openMenu")}</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Desktop menu */}
          <PopoverGroup className="hidden lg:flex lg:space-x-2">
            <Popover className="relative">
              <PopoverButton className="flex hover:bg-gray-300/60 duration-200 rounded-md px-4 py-2 items-center gap-x-1 text-sm/6 font-semibold outline-none">
                {t("nav.product")}
                <ChevronDownIcon
                  className="size-5 flex-none"
                  aria-hidden="true"
                />
              </PopoverButton>

              <PopoverPanel className="absolute -left-8 top-full z-10 mt-3 w-screen max-w-sm overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in">
                <div className="p-4">
                  {products.map((item) => (
                    <div
                      key={item.key}
                      className="group relative flex items-center gap-x-4 rounded-lg p-2 text-sm/6 hover:bg-gray-50"
                    >
                      <div className="flex size-11 flex-none items-center justify-center rounded-lg bg-neutral-800 group-hover:bg-neutral-900 duration-300">
                        <item.icon className="size-6 text-white group-hover:text-indigo-600 duration-300" />
                      </div>
                      <div className="flex-auto">
                        <Link
                          href={item.href}
                          className="block font-semibold text-gray-900"
                        >
                          {t(`nav.menu.${item.key}.name`)}
                          <p className="font-normal text-sm">
                            {t(`nav.menu.${item.key}.description`)}
                          </p>
                          <span className="absolute inset-0" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </PopoverPanel>
            </Popover>

            <Link
              href="/pricing"
              className="text-sm hover:bg-gray-300/60 duration-200 rounded-md px-4 py-2 font-semibold leading-6"
            >
              {t("nav.pricing")}
            </Link>
            <Link
              href="/resources"
              className="text-sm hover:bg-gray-300/60 duration-200 rounded-md px-4 py-2 font-semibold leading-6"
            >
              {t("nav.resources")}
            </Link>
            <Link
              href="/company"
              className="text-sm hover:bg-gray-300/60 duration-200 rounded-md px-4 py-2 font-semibold leading-6"
            >
              {t("nav.company")}
            </Link>
          </PopoverGroup>

          {/* Right-side desktop */}
          <div className="hidden lg:flex lg:flex-1 lg:justify-end items-center gap-x-6">
            <LanguageSwitcherFlags />
            {!loggedIn ? (
              <Link
                href="/login"
                className="hover:bg-gray-300/60 px-4 py-2 rounded-md text-sm font-semibold"
              >
                Login
              </Link>
            ) : (
              <Link
                href="/dashboard"
                className="hover:bg-gray-300/60 px-4 py-2 rounded-md flex items-center text-sm font-semibold"
              >
                Dashboard
                <ArrowLongRightIcon className="size-4 ml-1" />
              </Link>
            )}
          </div>
        </nav>
      </Slide>

      {/* Mobile menu panel */}
      <Dialog
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
        className="lg:hidden"
      >
        <div className="fixed inset-0 z-10" />
        <DialogPanel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <a href="#" className="-m-1.5 p-1.5">
              <span className="sr-only">OINKBIT</span>
              <Link href="/">
                <svg
                  className="h-6"
                  viewBox="0 0 585 121"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {" "}
                  <path
                    d="M359.943 18.941C358.96 19.3623 357.485 20.3454 356.642 21.1179C355.614 22.0573 355.027 23.3863 355.027 24.7795V68.0257V111.319C355.027 112.678 355.616 113.971 356.642 114.863C360.294 118.164 363.173 118.655 366.754 116.478C370.336 114.372 370.827 112.757 371.178 102.434C371.459 94.4289 371.67 92.9543 372.653 92.9543C373.566 92.9543 380.939 99.3444 388.663 106.647C391.964 109.878 399.829 116.408 401.163 117.11C405.236 119.217 411.064 116.619 412.188 112.054C413.241 107.771 411.836 104.751 406.429 100.117C403.831 97.94 400.32 94.78 398.565 93.2351C396.809 91.6201 393.438 88.6005 391.051 86.4939C387.118 83.0531 385.574 80.6655 387.189 80.6655C387.47 80.6655 388.944 79.4718 390.489 77.9971C392.034 76.5927 396.037 73.1519 399.337 70.343C410.011 61.3547 410.994 59.8098 408.676 54.9645C406.57 50.6108 401.444 48.5744 397.652 50.6108C395.756 51.5939 382.975 62.5484 377.147 68.1661C373.987 71.1856 372.793 71.9581 372.091 71.3963C371.389 70.8345 371.178 65.568 371.178 47.7317C371.178 26.244 371.108 24.6991 369.774 22.5925C368.369 20.205 365.069 18.1686 362.962 18.2388C362.26 18.2388 360.926 18.5899 359.943 18.941Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M429.671 19.573C425.106 22.3116 425.247 20.6965 425.247 68.1661C425.247 109.597 425.317 111.282 426.651 113.459C428.126 115.846 431.286 117.883 433.673 117.883C435.92 117.883 439.01 116.057 440.695 113.67C441.889 111.984 442.451 111.703 443.364 112.265C448.279 115.285 449.894 116.057 453.195 116.9C474.963 122.588 496.381 101.802 492.659 78.6291C491.184 69.4301 486.831 62.1973 479.528 56.6498C474.823 53.0685 472.646 52.0854 467.379 50.7512C459.655 48.8553 452.352 49.8384 446.102 53.4899C446.022 53.5375 445.942 53.5844 445.863 53.6307C443.662 54.9128 442.203 53.8794 442.115 51.3339C442.012 48.3497 441.928 43.9079 441.889 39.0243C441.749 21.4689 441.678 21.2583 436.693 19.1516C433.673 17.8174 432.339 17.8876 429.671 19.573ZM468.011 67.6043C475.736 72.5198 479.387 82.8424 476.017 90.2858C474.261 94.148 470.61 98.5719 467.52 100.468C465.273 101.872 464.009 102.083 458.602 102.083C452.984 102.083 452.001 101.872 449.473 100.257C446.383 98.2208 444.557 96.1844 442.451 92.5329C441.397 90.6369 441.117 88.8112 441.117 83.8255C441.117 78.8398 441.397 77.014 442.451 75.0478C444.066 72.239 447.928 68.0257 448.981 68.0257C449.332 68.0257 450.035 67.6043 450.526 67.0426C452.492 64.6551 463.939 65.0062 468.011 67.6043Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M235.789 25.6121C228.837 31.0894 230.593 40.9906 238.879 43.4484C247.025 45.9061 254.609 36.9178 250.746 29.4041C247.867 23.8566 240.424 21.9606 235.789 25.6121Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M511.619 26.4548C508.74 29.053 508.599 29.2636 508.529 33.3365C508.459 38.4626 510.355 41.3417 514.99 43.1675C525.172 47.1701 533.107 33.6876 524.891 26.2441C522.503 24.0673 521.731 23.7864 518.36 23.7864C514.99 23.7864 514.287 24.0673 511.619 26.4548Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M557.614 34.1089C553.752 35.092 552.066 38.252 551.645 45.2039L551.294 50.8216L547.572 51.1727C542.376 51.7345 540.059 54.122 540.059 58.897C540.059 63.5316 542.165 65.7085 546.87 66.2C548.696 66.4107 550.522 66.6916 550.943 66.9725C551.364 67.2534 551.645 73.5031 551.645 84.0363C551.645 103.066 551.996 105.032 556.42 110.159C560.633 115.074 564.215 116.479 572.501 116.479C578.891 116.479 579.242 116.408 581.138 114.442C584.579 111.001 584.79 107.069 581.63 103.417C580.225 101.802 579.312 101.521 575.029 101.24C570.886 100.889 569.903 100.608 569.271 99.3445C568.709 98.3614 568.498 92.8841 568.639 82.4212L568.849 66.9725L575.099 66.4809C580.576 66.0596 581.559 65.7787 583.174 64.1636C584.649 62.7592 585 61.7059 585 58.897C585 56.018 584.649 55.1051 583.034 53.49C581.279 51.7345 580.506 51.5238 574.959 51.1727L568.849 50.8216L568.498 44.6421C568.147 39.0244 567.936 38.3924 566.04 36.4964C563.934 34.3898 560.353 33.4067 557.614 34.1089Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M173.502 50.7514C164.163 52.858 155.175 60.1611 150.68 69.1494C148.363 73.9244 148.293 74.1351 148.223 83.1234C148.223 90.1456 148.504 92.9544 149.416 95.0611C150.048 96.6059 150.751 98.3615 151.032 98.9232C152.998 103.769 160.371 111.282 165.848 114.021C178.628 120.411 194.99 118.445 204.821 109.316C207.63 106.648 211.773 101.521 211.773 100.538C211.773 100.187 212.124 99.5552 212.545 99.1339C215.916 95.7633 217.25 80.2444 214.722 73.2924C208.683 56.1584 191.339 46.6786 173.502 50.7514ZM190.004 66.9725C190.636 67.5343 191.339 68.0258 191.619 68.0258C192.181 68.0258 197.026 72.8711 197.026 73.4329C197.026 73.7138 197.448 74.416 197.939 74.9778C198.501 75.6098 199.274 77.7164 199.695 79.7528C201.45 87.6176 197.799 95.6931 190.355 100.679C188.67 101.802 187.055 102.083 181.929 102.083C176.241 102.083 175.328 101.873 172.66 100.187C167.393 96.7464 163.32 89.4433 163.32 83.3341C163.32 76.0311 170.061 67.3939 177.013 65.7788C180.314 65.0766 188.74 65.849 190.004 66.9725Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M294.283 50.8218C282.275 53.8413 273.568 62.4083 269.776 74.9779C268.863 77.9974 268.652 82.0703 268.652 95.5528V109.129C268.652 111.227 269.486 113.239 270.97 114.723C272.304 116.057 274.2 117.321 275.253 117.602C278.273 118.304 282.486 116.619 284.101 114.021C285.365 111.914 285.505 110.44 285.505 95.5528C285.505 81.7192 285.646 79.0508 286.769 76.6632C288.665 72.4499 291.053 69.5006 294.213 67.5345C296.741 65.8491 297.864 65.6385 303.061 65.6385C308.327 65.5683 309.381 65.8491 311.838 67.394C315.35 69.7113 316.543 71.0455 318.65 75.0481C320.195 78.0677 320.265 79.0508 320.616 95.4825C320.967 114.513 320.967 114.513 325.672 116.9C329.394 118.796 333.677 117.392 336.065 113.459C337.329 111.423 337.469 109.878 337.469 95.4123C337.399 75.1886 336.627 71.6775 330.658 63.6723C322.091 52.3666 307.906 47.3809 294.283 50.8218Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M237.276 52.1766C236.433 52.7384 235.099 54.2833 234.326 55.6175C232.922 57.8646 232.852 58.8477 232.852 84.338C232.852 109.969 232.922 110.811 234.326 113.269C237.627 118.817 243.876 119.378 248.09 114.393C249.634 112.567 249.634 112.427 249.705 84.338V61.9289C249.705 58.9679 248.851 56.0697 247.247 53.5811C245.113 51.4598 244.227 51.1233 241.77 51.1233C240.155 51.1233 238.118 51.6149 237.276 52.1766Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M513.024 53.2092C511.467 54.4991 510.566 56.4156 510.566 58.4374V84.3172C510.566 112.406 510.637 113.389 511.971 114.793C515.201 118.164 518.923 118.796 522.715 116.479C523.979 115.706 525.313 114.231 525.734 113.178C526.156 112.055 526.366 100.96 526.226 83.2639L526.039 58.4373C526.024 56.4137 525.116 54.5003 523.557 53.2092C520.257 50.4003 516.325 50.4003 513.024 53.2092Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M60.482 0H120.769V60.9621L112.035 59.8891C81.9829 56.1974 59.6616 30.2665 60.482 0Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M61.1562 59.2258L0.0012207 59.2258L0.0012207 -2.28882e-05L1.92944 0.00606918C34.5939 0.109272 61.0492 26.5614 61.1562 59.2258Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M120.766 59.9563L120.766 120.574H59.8035L60.9769 111.554C64.868 81.6441 90.6078 59.4305 120.766 59.9563Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M61.155 59.8046C61.155 76.852 47.465 90.6715 30.5775 90.6715C13.69 90.6715 0 76.852 0 59.8046C0 42.7573 13.69 28.9377 30.5775 28.9377C47.465 28.9377 61.155 42.7573 61.155 59.8046Z"
                    fill="currentColor"
                  />{" "}
                  <path
                    d="M0.582031 120.574C0.582031 103.855 14.1354 90.0928 30.8543 90.0928C47.5732 90.0928 61.1266 103.855 61.1266 120.574H0.582031Z"
                    fill="currentColor"
                  />{" "}
                </svg>
              </Link>
            </a>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">{t("nav.closeMenu")}</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {products.map((item) => (
                  <Link
                    key={item.key}
                    href={item.href}
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                  >
                    {t(`nav.menu.${item.key}.name`)}
                  </Link>
                ))}
                <Link
                  href="/pricing"
                  className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                >
                  {t("nav.pricing")}
                </Link>
                <Link
                  href="/resources"
                  className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                >
                  {t("nav.resources")}
                </Link>
                <Link
                  href="/company"
                  className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                >
                  {t("nav.company")}
                </Link>
              </div>
              <div className="py-6 space-y-4">
                {loggedIn ? (
                  <Link
                    href="/dashboard/"
                    className="text-base font-semibold leading-6 text-gray-900"
                  >
                    {t("nav.dashboard")}
                  </Link>
                ) : (
                  <Link
                    href="/login"
                    className="text-base font-semibold leading-6 text-gray-900"
                  >
                    {t("nav.login")}
                  </Link>
                )}
                <LanguageSwitcherFlags />
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}
